import React, { ReactNode, useMemo } from 'react'

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@barracuda-internal/bds-core'
import styles from 'components/libs/dialog/warningDialog/warningDialogStyles'
import { useFormatMessage } from 'lib/localization'
import { useWarningDialogLogic } from 'components/libs/dialog/warningDialog/useWarningDialogLogic'
import { DIALOG_Z_INDEX } from 'components/libs/dialog/dialogStyles'

const BASE_I18N_KEY = 'ess.dialog'

export interface WarningDialogProps {
  title: string
  description?: ReactNode
  onCancel: () => void
  onConfirm?: () => void
  open?: boolean
  onClose?: () => void
  isPending?: boolean
  cancelButtonLabel?: string
  confirmButtonLabel?: string
}

const WarningDialog: React.FC<WarningDialogProps> = ({
  title,
  description,
  onConfirm,
  onCancel,
  open,
  onClose,
  isPending,
  cancelButtonLabel,
  confirmButtonLabel
}) => {
  const isOpen = typeof open === 'undefined' ? true : open
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const classes = styles()
  const logic = useWarningDialogLogic({ isPending, onCancel, onConfirm, onClose })

  return useMemo(
    () => (
      <Dialog
        maxWidth="sm"
        open={isOpen}
        data-testid="root-dialog"
        onClose={logic.onClose}
        style={{ zIndex: DIALOG_Z_INDEX.STANDARD }}
      >
        <DialogTitle data-testid="title">{title}</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Grid className={classes.content} container direction="row" alignItems="center">
            <Grid>
              {typeof description === 'string' && !!description && (
                <Typography data-testid="description">{description}</Typography>
              )}
              {typeof description !== 'string' && !!description && description}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={logic.onCancel} data-testid="cancel-button" disabled={isPending}>
            {cancelButtonLabel || formatMessage('cancel')}
          </Button>
          {logic.onConfirm && (
            <Button
              variant="contained"
              color="primary"
              onClick={logic.onConfirm}
              data-testid="confirm-button"
              disabled={isPending}
              isLoading={isPending}
            >
              {confirmButtonLabel || formatMessage('ok')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    ),
    [isOpen, logic, classes, title, description, cancelButtonLabel, confirmButtonLabel, formatMessage, isPending]
  )
}

export default WarningDialog
